/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  getInvalidFields,
  isEntityInvalid,
  setEntityField,
  setEntityFields,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { find, first, includes } from 'lodash';
import {
  getHpeComponentId,
  getHpeCostCenterId,
  getHpeCuttingListItemId,
  getHpeItemId,
  getHpeTopId,
  HpeComponent,
  HpeCuttingListItem,
  HpeItem,
  HpeTop,
  isHpeComponentCostCenter,
  isHpeElementHasInvalidFormula,
} from '../../../resources/hpe-details.model';
import {
  hpeCcTable,
  HpeComponentDetailsTab,
  hpeComponentTable,
  HpeDetailsSelection,
  HpeDetailsState,
  HpeDetailsStateEvent,
  HpeDetailsStateEventType,
  HpeFocusedTable,
  hpeItemTable,
  queryHpeBestComponentDetailsTab,
  queryHpeBestItemDetailsTab,
  queryHpeCcHasErrors,
  queryHpeFirstInvalidComponent,
  queryHpeFirstInvalidItem,
  queryHpeFirstInvalidTop,
  queryHpeItem,
  queryHpeSelectedElement,
  queryHpeTop,
} from '../hpe-details.state';

const highlightInvalidFormula = (selection: HpeDetailsSelection, highlight: boolean) =>
  setEntityField(selection, 'highlightInvalidFormula', highlight);

const selectCC = (selection: HpeDetailsSelection, ccId?: ID) => setEntityField(selection, 'costCenterId', ccId);
const selectCCItem = (selection: HpeDetailsSelection, itemId?: ID) =>
  setEntityFields(selection, { itemId, componentId: void 0 });
const selectComponent = (selection: HpeDetailsSelection, componentId?: ID) =>
  setEntityFields(selection, { componentId, itemId: void 0 });
const selectDetailsTab = (selection: HpeDetailsSelection, name?: string) =>
  highlightInvalidFormula(setEntityField(selection, 'detailsTabName', name), false);
const setFocusedCell = (selection: HpeDetailsSelection, table?: HpeFocusedTable, cell?: FocusedCell) =>
  setEntityFields(selection, {
    'focusedTable': table,
    'focusedCell': cell,
  });
const resetFocusedCell = (selection: HpeDetailsSelection) => setFocusedCell(selection, void 0, void 0);

export function selectionReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  return setEntityField(state, 'selection', navigate(state, state.selection, event));
}

/**
 * Guarantees that selection always hold references to existing entities.
 * It is important to remove references to removed entities.
 */
export function selectionConsistencyReducer(state: HpeDetailsState, event: HpeDetailsStateEvent): HpeDetailsState {
  const { selection } = state;
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterItemAddSet: {
      const itemToSelect = first(event.itemSet);
      const itemId = itemToSelect ? getHpeItemId(itemToSelect) : void 0;
      return setEntityField(state, 'selection', navigateToCCItem(state, state.selection, itemId));
    }
    case HpeDetailsStateEventType.CostCenterItemRemove:
      if (selection.itemId === event.itemId) {
        return setEntityField(state, 'selection', selectCCItem(selection));
      } else {
        return state;
      }
    case HpeDetailsStateEventType.ComponentAddSet: {
      const componentToSelect = first(event.componentSet.components);
      const componentId = componentToSelect ? getHpeComponentId(componentToSelect) : void 0;
      return setEntityField(state, 'selection', navigateToComponent(state, state.selection, componentId));
    }
    case HpeDetailsStateEventType.ComponentRemove:
      if (selection.componentId === event.componentId) {
        return setEntityField(state, 'selection', selectComponent(selection));
      } else {
        return state;
      }
    case HpeDetailsStateEventType.CostCenterRemove:
      if (selection.costCenterId === event.costCenterId) {
        return setEntityField(state, 'selection', selectCC(selection));
      } else {
        return state;
      }
    default:
      return state;
  }
}

function navigate(state: HpeDetailsState,
                  selection: HpeDetailsSelection,
                  event: HpeDetailsStateEvent): HpeDetailsSelection {
  switch (event.type) {
    case HpeDetailsStateEventType.CostCenterSelect: {
      const costCenter = event.costCenterId && hpeCcTable.byId(state.hpCostCenters, event.costCenterId);

      if (costCenter && isHpeComponentCostCenter(costCenter)) {
        return naviateToComponentsCC(state, selection);
      } else if (costCenter) {
        return navigateToCC(state, selection, event.costCenterId);
      } else {
        return state.selection;
      }
    }
    case HpeDetailsStateEventType.ComponentSelect:
      return navigateToComponent(state, selection, event.componentId);
    case HpeDetailsStateEventType.CostCenterItemSelect:
      return navigateToCCItem(state, selection, event.itemId);
    case HpeDetailsStateEventType.DetailsTabSelect:
      return selectDetailsTab(selection, event.name);
    case HpeDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case HpeDetailsStateEventType.NavigateToInvalidTop: {
      const top = queryHpeTop(state, event.topId);
      return top ? navigateToInvalidTop(state, top) : selection;
    }
    case HpeDetailsStateEventType.HighlightInvalid:
      return highlightInvalid(state);
    case HpeDetailsStateEventType.ResetFocusedCell:
      return resetFocusedCell(state.selection);
    default:
      return selection;
  }
}

function navigateToCC(state: HpeDetailsState,
                      selection: HpeDetailsSelection,
                      costCenterId?: ID): HpeDetailsSelection {
  const firstItem = first(hpeItemTable.byGroup(state.hpItems, 'costCenter', costCenterId));

  return navigateToCCItem(state, selectCC(selection, costCenterId), firstItem ? getHpeItemId(firstItem) : void 0);
}

function navigateToCCItem(state: HpeDetailsState,
                          selection: HpeDetailsSelection,
                          itemId?: ID): HpeDetailsSelection {
  const item = itemId && queryHpeItem(state, itemId);

  if (item) {
    return resetFocusedCell(
      selectDetailsTab(
        selectCCItem(selectCC(selection, item.costCenterId), itemId),
        queryHpeBestItemDetailsTab(state, itemId)));
  } else {
    return resetFocusedCell(
      selectDetailsTab(
        selectCCItem(selection, void 0),
        void 0));
  }
}

function naviateToComponentsCC(state: HpeDetailsState,
                               selection: HpeDetailsSelection): HpeDetailsSelection {
  // select first component
  const firstComponent = first(hpeComponentTable.byOrder(state.hpComponents, 'code'));

  return navigateToComponent(
    state,
    selection,
    firstComponent ? getHpeComponentId(firstComponent) : void 0);
}

function navigateToComponent(state: HpeDetailsState,
                             selection: HpeDetailsSelection,
                             componentId: Maybe<ID>): HpeDetailsSelection {
  return resetFocusedCell(
    selectDetailsTab(
      selectComponent(selectCC(selection, state.hpComponentsCcId), componentId),
      queryHpeBestComponentDetailsTab(state, componentId)));
}

function navigateToInvalid(state: HpeDetailsState): HpeDetailsSelection {
  const invalidTop = queryHpeFirstInvalidTop(state);
  if (invalidTop) {
    return navigateToInvalidTop(state, invalidTop);
  }

  const invalidComponent = queryHpeFirstInvalidComponent(state);
  if (invalidComponent) {
    return navigateToInvalidComponent(state, invalidComponent);
  }

  const invalidCC = find(hpeCcTable.all(state.hpCostCenters),
                         (cc) => queryHpeCcHasErrors(state, getHpeCostCenterId(cc)));
  if (invalidCC) {
    const invalidItem = queryHpeFirstInvalidItem(state, getHpeCostCenterId(invalidCC));
    if (invalidItem) {
      return navigateToInvalidCCItem(state, invalidItem);
    }
  }

  return state.selection;
}

function navigateToInvalidTop(state: HpeDetailsState, top: HpeTop): HpeDetailsSelection {
  return setFocusedCell(
    state.selection,
    HpeFocusedTable.TopList,
    { id: getHpeTopId(top), field: 'value' });
}

function navigateToInvalidComponent(state: HpeDetailsState, component: HpeComponent): HpeDetailsSelection {
  const invalidFields = getInvalidFields(component);

  const componentSelection = selectDetailsTab(
    selectComponent(
      selectCC(state.selection, state.hpComponentsCcId),
      getHpeComponentId(component)),
    queryHpeBestComponentDetailsTab(state, getHpeComponentId(component)));

  if (includes(invalidFields, 'quantity')) {
    return setFocusedCell(
      componentSelection,
      HpeFocusedTable.ComponentList,
      { id: getHpeComponentId(component), field: 'quantity' });
  } else if (includes(invalidFields, 'formula')) {
    return highlightInvalidFormula(selectDetailsTab(componentSelection, HpeComponentDetailsTab.Formula), true);
  } else if (includes(invalidFields, 'cuttingList')) {
    return navigateToInvalidCuttingListItem(
      selectDetailsTab(componentSelection, HpeComponentDetailsTab.CuttingList),
      component.cuttingList || []);
  } else {
    return componentSelection;
  }
}

function navigateToInvalidCCItem(state: HpeDetailsState, item: HpeItem): HpeDetailsSelection {
  const invalidFields = getInvalidFields(item);

  const itemSelection = selectDetailsTab(
    selectCCItem(
      selectCC(state.selection, item.costCenterId),
      getHpeItemId(item)),
    queryHpeBestItemDetailsTab(state, getHpeItemId(item)));

  if (includes(invalidFields, 'quantity')) {
    return setFocusedCell(
      itemSelection,
      HpeFocusedTable.ItemList,
      { id: getHpeItemId(item), field: 'quantity' });
  } else if (includes(invalidFields, 'formula')) {
    return highlightInvalidFormula(selectDetailsTab(itemSelection, HpeComponentDetailsTab.Formula), true);
  } else if (includes(invalidFields, 'cuttingList')) {
    return navigateToInvalidCuttingListItem(
      selectDetailsTab(itemSelection, HpeComponentDetailsTab.CuttingList),
      item.cuttingList || []);
  } else {
    return itemSelection;
  }
}

function navigateToInvalidCuttingListItem(selection: HpeDetailsSelection,
                                          cuttingList: HpeCuttingListItem[]): HpeDetailsSelection {
  const cuttingListItem = find<HpeCuttingListItem>(cuttingList, isEntityInvalid);

  if (cuttingListItem) {
    return setFocusedCell(selection, HpeFocusedTable.CuttingList, {
      id: getHpeCuttingListItemId(cuttingListItem),
      field: getInvalidFields(cuttingListItem)[0],
    });
  } else {
    return selection;
  }
}

/**
 * Highlights invalid data in current selection
 */
function highlightInvalid(state: HpeDetailsState): HpeDetailsSelection {
  const selectedStructure = queryHpeSelectedElement(state);

  if (isNotNil(selectedStructure) && isHpeElementHasInvalidFormula(selectedStructure)) {
    return highlightInvalidFormula(state.selection, true);
  } else {
    return state.selection;
  }
}
